import { TargetState } from "@uirouter/angular";
import { ApmBase, apm } from "@elastic/apm-rum";
import { EnvironmentService, IAppConfig } from "@gtmhub/env";
import { getCurrentUserId } from "@gtmhub/users";
import { sanitizeHref } from "@webapp/core/logging/logging.utils";
import { storage } from "../storage";
import {
  ANGULAR_PAGE_LOAD_TRANSACTION_TYPE,
  ApmTransaction,
  ApmTransactionProxy,
  BLACKLIST,
  BLOCKING_SPAN_NAME,
  CoreApmSpan,
  CoreApmTransaction,
  DATA_LOAD_SPAN_TYPE,
  DUPLICATED_SPAN_TYPE,
  IApmService,
  PAGE_LOAD_TRANSACTION_TYPE,
  PENDING_DATA_LOAD_SPAN_TYPE,
  PENDING_RENDER_SPAN_TYPE,
  RENDER_ENDED_MARK_NAME,
  RENDER_SPAN_TYPE,
  ROUTE_CHANGE_BLOCKING_SPAN_TYPE,
  ROUTE_CHANGE_TRANSACTION_TYPE,
  SHORT_OFFSET,
  TIMER_END_OFFSET,
} from "./apm.models";

let isPerformanceTest: boolean;
window.setRUMPerfTestLabels = (labels) => {
  isPerformanceTest = true;
  ApmService.setPerfTestLabels(labels);
};

export class ApmService implements IApmService {
  private static apmInstance: ApmBase | null;
  private static currentTransaction: ApmTransactionProxy;
  private static nonManagedTransaction: ApmTransactionProxy;
  private lastCustomSpanName: string;
  private visitedStates: TargetState[] = [];
  private accountIdAdded = false;

  constructor(private appConfig: IAppConfig, env: EnvironmentService) {
    ApmService.apmInstance = apm.init({
      serviceName: "gtmhub-frontend",
      serverUrl: this.appConfig.rum.url,
      propagateTracestate: true,
      breakdownMetrics: true,
      distributedTracingOrigins: [env.getWebappEndpoint()],
      environment: this.appConfig.rum.environment,
      disableInstrumentations: ["eventtarget"],
    });
    this.setAccountId();

    const currentUserId = getCurrentUserId();
    if (currentUserId) {
      this.setUserContext({ id: currentUserId });
    }

    const sanitizedUrl = sanitizeHref(window.location.href);
    ApmService.apmInstance.setInitialPageLoadName(sanitizedUrl);
    ApmService.apmInstance.observe("transaction:end", (transaction) => {
      this.setAccountId();
      const apmTransaction: CoreApmTransaction = transaction as unknown as CoreApmTransaction;

      this.setPerformanceStats(apmTransaction);
      if (this.isRouteChangeOrPageLoad(apmTransaction)) {
        const lastCustomSpan = apmTransaction.spans.find((span) => span.name === this.lastCustomSpanName) as CoreApmSpan;
        let maxSpanEnd = 0;

        apmTransaction.spans.forEach((apmSpan) => {
          const span: CoreApmSpan = apmSpan as CoreApmSpan;

          this.spanApplyAction(span, lastCustomSpan);

          if (span._end > maxSpanEnd) {
            maxSpanEnd = span._end;
          }
        });

        if (apmTransaction._start > maxSpanEnd) {
          apmTransaction._start = maxSpanEnd;
        }

        apmTransaction._end = maxSpanEnd;

        this.markRenderEnd(apmTransaction, { pageLoadTime: maxSpanEnd });
      }

      if (isPerformanceTest) {
        // eslint-disable-next-line no-console
        console.log(`${apmTransaction.type}, id:${apmTransaction.id}`);
      }
    });
  }

  private setPerformanceStats(apmTransaction: CoreApmTransaction) {
    const connectionInfo = window.navigator?.connection;
    const memory = window.performance?.memory;
    if (connectionInfo) {
      apmTransaction.addLabels({ rttMs: connectionInfo.rtt });
      apmTransaction.addLabels({ downlinkMbps: connectionInfo.downlink });
      apmTransaction.addLabels({ connectionEffectiveType: connectionInfo.effectiveType });
    }
    if (memory) {
      apmTransaction.addLabels({ jsHeapSizeLimit: memory.jsHeapSizeLimit });
      apmTransaction.addLabels({ usedJSHeapSize: memory.usedJSHeapSize });
      apmTransaction.addLabels({ totalJSHeapSize: memory.totalJSHeapSize });
    }
  }

  private setAccountId(): void {
    if (this.accountIdAdded) return;
    const accountId = storage.get("accountId");
    if (accountId) {
      ApmService.apmInstance.addLabels({
        accountId: accountId as string,
      });
      this.accountIdAdded = true;
    }
  }

  public captureError(error: string | Error): void {
    ApmService.apmInstance.captureError(error);
  }

  public static setPerfTestLabels(labels: Record<string, string>): void {
    ApmService.apmInstance.addLabels(labels);
  }

  public setUserContext(context: UserObject): void {
    ApmService.apmInstance.setUserContext(context);
  }

  public addLabelsToCurrentTransactions(labels: Record<string, string>): void {
    for (const transaction of this.getOpenedTransactions()) {
      transaction.addLabels(labels);
    }
  }

  public addMarkToCurrentTransactions(key: string): void {
    for (const transaction of this.getOpenedTransactions()) {
      transaction.mark(key);
    }
  }

  public startTransactionForState(state: TargetState): void {
    if (!this.visitedStates.some((s) => s.name() === state.name())) {
      this.visitedStates.push(state);
    }

    const nonManagedTransactionHasNotEnded = ApmService.nonManagedTransaction && !ApmService.nonManagedTransaction.ended;
    if (nonManagedTransactionHasNotEnded && !this.isStillOnFirstState(this.visitedStates)) {
      ApmService.nonManagedTransaction.addLabels({ stateChangedTo: state.name() });

      ApmService.nonManagedTransaction.end();
      ApmService.nonManagedTransaction = null;
    }

    let apmTransaction: ApmTransaction;
    const currentTransaction = ApmService.apmInstance.getCurrentTransaction();
    if (currentTransaction && currentTransaction.type === PAGE_LOAD_TRANSACTION_TYPE && this.isStillOnFirstState(this.visitedStates)) {
      currentTransaction.name = state.name();
      apmTransaction = currentTransaction;
    } else {
      if (currentTransaction) {
        currentTransaction.addLabels({ stateChangedTo: state.name() });
      }

      this.visitedStates = [state];
      apmTransaction = ApmService.apmInstance.startTransaction(state.name(), ROUTE_CHANGE_TRANSACTION_TYPE, {
        managed: true,
      });
    }

    if (this.visitedStates.length === 1) {
      ApmService.currentTransaction = this.setupTransactionProxy(apmTransaction);
      ApmService.currentTransaction.startSpanWithName(BLOCKING_SPAN_NAME, ROUTE_CHANGE_BLOCKING_SPAN_TYPE, { blocking: true });
    }

    if (state.state()?.data?.ignoreApm) {
      ApmService.currentTransaction?.startSpanWithName(`noninteractive-state`, TIMER_END_OFFSET);
    } else {
      ApmService.currentTransaction?.endSpanByName(`noninteractive-state`, TIMER_END_OFFSET);
    }
  }

  public endCurrentTransaction(): void {
    const currentTransaction = this.getCurrentManagedTransaction();
    if (currentTransaction && currentTransaction.type !== PAGE_LOAD_TRANSACTION_TYPE && !currentTransaction.customSpans) {
      currentTransaction.end();
    }
  }

  public starTimerEndOffsetSpan(name: string): void {
    this.startSpan(name, TIMER_END_OFFSET);
  }

  public endTimerEndOffsetSpan(name: string): void {
    this.endSpan(name, TIMER_END_OFFSET);
  }

  public startDataLoadSpan(name: string): void {
    this.startSpan(name, PENDING_DATA_LOAD_SPAN_TYPE);
  }

  public startRenderSpan(name: string): void {
    this.startSpan(name, PENDING_RENDER_SPAN_TYPE);
  }

  public endDataLoadSpan(name: string): void {
    this.endSpan(name, DATA_LOAD_SPAN_TYPE);
  }

  public endRenderSpan(name: string): void {
    this.endSpan(name, RENDER_SPAN_TYPE);
  }

  private isStillOnFirstState(visitedStates: TargetState[]): boolean {
    return visitedStates.filter((s) => !s.state()?.data?.ignoreApm).length <= 1;
  }

  private startNonManagedTransaction(): ApmTransactionProxy {
    const { name, id, type } = (ApmService.apmInstance.getCurrentTransaction() as unknown as CoreApmTransaction) || {};
    const nonManagedTransaction = ApmService.apmInstance.startTransaction(name, ANGULAR_PAGE_LOAD_TRANSACTION_TYPE, {
      managed: false,
    });

    const transaction = this.setupTransactionProxy(nonManagedTransaction);
    ApmService.nonManagedTransaction = transaction;

    transaction.resetTransactionEndTimer(SHORT_OFFSET);
    transaction.addLabels({ managedTransactionId: id, managedTransactionType: type });

    return transaction;
  }

  private setupTransactionProxy(apmTransaction: ApmTransaction): ApmTransactionProxy {
    const transaction = ApmTransactionProxy.newInstance(apmTransaction);
    apmTransaction.addLabels(this.getMandatoryLabels(transaction));

    return transaction;
  }

  private getCurrentManagedTransaction(): ApmTransactionProxy {
    return ApmService.currentTransaction;
  }

  private getOpenedTransactions(): ApmTransactionProxy[] {
    return [ApmService.currentTransaction, ApmService.nonManagedTransaction].filter((x) => !!x);
  }

  private startSpan(name: string, type: string): void {
    if (!ApmService.nonManagedTransaction || ApmService.nonManagedTransaction.ended) {
      this.startNonManagedTransaction();
    }

    for (const transaction of this.getOpenedTransactions()) {
      transaction.customSpans = true;
      transaction.startSpanWithName(name, type, { labels: this.getMandatoryLabels(transaction) });
    }
  }

  private endSpan(name: string, type: string): void {
    for (const transaction of this.getOpenedTransactions()) {
      this.lastCustomSpanName = name;
      transaction.endSpanByName(name, type);
    }
  }

  private spanApplyAction(span: CoreApmSpan, lastCustomSpan: CoreApmSpan): void {
    if (lastCustomSpan && span._start > lastCustomSpan._end) {
      this.markForExclusion(span);
    }
    if (span.type.indexOf(DUPLICATED_SPAN_TYPE) != -1 || span.type.indexOf(ROUTE_CHANGE_BLOCKING_SPAN_TYPE) != -1) {
      this.markForExclusion(span);
    }
    if (span.context && span.context.destination && BLACKLIST[span.context.destination.address]) {
      this.markForExclusion(span);
    }
    if (span.type.indexOf(PENDING_RENDER_SPAN_TYPE) != -1) {
      this.adjustSpan(span, PENDING_RENDER_SPAN_TYPE);
    }
    if (span.type.indexOf(PENDING_DATA_LOAD_SPAN_TYPE) != -1) {
      this.adjustSpan(span, PENDING_DATA_LOAD_SPAN_TYPE);
    }
  }

  private adjustSpan(span: CoreApmSpan, type: string): void {
    span._end = span._start + 1;
    span.name = span.name + " - not resolved";
    span.type = type;
  }

  private markForExclusion(span: CoreApmSpan): void {
    span._end = 0;
  }

  private markRenderEnd(apmTransaction: CoreApmTransaction, { pageLoadTime }: { pageLoadTime: number }): void {
    performance.mark(RENDER_ENDED_MARK_NAME, {
      detail: {
        pageLoadTime,
        transactionId: apmTransaction.id,
      },
    });
  }

  private isRouteChangeOrPageLoad(apmTransaction: CoreApmTransaction): boolean {
    return (
      apmTransaction.type === PAGE_LOAD_TRANSACTION_TYPE ||
      apmTransaction.type === ROUTE_CHANGE_TRANSACTION_TYPE ||
      apmTransaction.type === ANGULAR_PAGE_LOAD_TRANSACTION_TYPE
    );
  }

  private getMandatoryLabels(transaction: ApmTransactionProxy): Record<"transactionType" | "transactionName", string> {
    return { transactionName: transaction.name, transactionType: transaction.type };
  }
}
